import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Form } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { ONE_HOUR_IN_MS } from '@careerstart/wae-common/src/main/constants/timeConversions';
import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import { Box, Button, CircularProgress, Divider, Typography } from '@mui/material';

import { dispatchErrors } from '../../../datahub/errorUtils';
import selectUser from '../../store/selectors/appSelector';
import { PRIMARY_COLOR } from '../../theme/colorConstants';
import { PRIMARY_FONT } from '../../theme/fontConstants';
import { showSnackbar } from '../app';

import {
  CorporationAgencySection,
  DateSection,
  MarginOverrideSection,
  PositionTemplatesSection,
  RateOverridesSection,
  StipendSection,
} from './components';
import { CONTRACT_TYPES } from './constants';
import { ContractApiService, PositionTemplateApiService } from './services';
import { ERROR_MESSAGES } from './validationConstants';

const ContractCreate = () => {
  const navigate = useNavigate();
  const { userId, contractType } = useParams();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);

  const [selectedPositionTemplates, setSelectedPositionTemplates] = useState([]);
  const [availablePositionTemplates, setAvailablePositionTemplates] = useState([]);
  const [hasStipend, setHasStipend] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentCorporationId, setCurrentCorporationId] = useState(null);

  const handleBack = () => {
    navigate(`/users/${userId}?role=candidate`);
  };

  const handleSubmit = async (values) => {
    setIsSubmitting(true);

    try {
      // Validate required fields before submission
      if (!values.agency?._id) {
        dispatch(showSnackbar({ message: ERROR_MESSAGES.REQUIRED_FIELD }));
        setIsSubmitting(false);
        return;
      }

      // Prepare the API payload according to the schema and contract type
      const payload = {
        fields: {
          contractScope: {
            workerId: userId,
            corporationId: values.corporation._id,
            positionTemplateIds: selectedPositionTemplates.map((pt) => pt._id),
            agencyId: values.agency._id,
          },
          rates: {
            type: contractType,
            ...(contractType === CONTRACT_TYPES.RATE_OVERRIDE && {
              fixedRates: {
                pay: {
                  reg: parseFloat(values.payRate),
                  OT: parseFloat(values.otPayRate),
                },
                bill: {
                  reg: parseFloat(values.billRate),
                  OT: parseFloat(values.otBillRate),
                },
              },
            }),
            ...(contractType === CONTRACT_TYPES.MARGIN_OVERRIDE && {
              marginRates: {
                reg: parseFloat(values.marginRate) / 100,
                OT: parseFloat(values.otMarginRate) / 100,
              },
            }),
          },
          timeWindow: {
            start: values.startDate ? Math.floor(values.startDate.getTime()) : null,
            ...(values.expirationDate && {
              end: Math.floor(values.expirationDate.getTime()),
            }),
          },
          // Include stipend data if stipend is enabled
          ...(hasStipend && {
            stipend: {
              mealAmount: parseFloat(values.mealAmount) || 0,
              lodgingAmount: parseFloat(values.lodgingAmount) || 0,
              targetTimeAccrued: values.targetHours
                ? parseFloat(values.targetHours) * ONE_HOUR_IN_MS
                : 0, // Convert hours to milliseconds
              prorateDropoffTimeAccrued: values.prorateDropOff
                ? parseFloat(values.prorateDropOff) * ONE_HOUR_IN_MS
                : 0, // Convert hours to milliseconds
            },
          }),
        },
      };

      await ContractApiService.createContract(
        payload,
        user,
        dispatch,
        () => {
          dispatch(showSnackbar({ message: ERROR_MESSAGES.CONTRACT_CREATE_SUCCESS }));
          // Success - navigate back
          handleBack();
        },
        (d) => {
          dispatchErrors({ dispatch, responsePayload: d.response });
          setIsSubmitting(false);
        }
      );
    } catch (error) {
      dispatch(showSnackbar({ message: ERROR_MESSAGES.CONTRACT_CREATE_ERROR }));
      setIsSubmitting(false);
    }
  };

  // Function to load position templates for a corporation
  const loadPositionTemplates = useCallback(
    async (corporationId) => {
      if (!corporationId) return;

      try {
        await PositionTemplateApiService.loadPositionTemplates(
          corporationId,
          user,
          dispatch,
          (response) => {
            const templates = response?.data?.documents || [];
            setAvailablePositionTemplates(templates);
          },
          (d) => {
            dispatchErrors({ dispatch, responsePayload: d.response });
            setAvailablePositionTemplates([]);
          }
        );
      } catch {
        dispatch(
          showSnackbar({
            message: ERROR_MESSAGES.POSITION_TEMPLATES_LOAD_ERROR,
          })
        );
        setAvailablePositionTemplates([]);
      }
    },
    [dispatch, user]
  );

  // Handle position template selection
  const handlePositionTemplateToggle = (template) => {
    setSelectedPositionTemplates((prev) => {
      const isSelected = prev.some((pt) => pt._id === template._id);
      if (isSelected) {
        return prev.filter((pt) => pt._id !== template._id);
      }
      return [...prev, template];
    });
  };

  // Handle Select All functionality
  const handleSelectAll = () => {
    setSelectedPositionTemplates([...availablePositionTemplates]);
  };

  // Handle Clear functionality
  const handleClear = () => {
    setSelectedPositionTemplates([]);
  };

  // Load position templates when corporation changes
  useEffect(() => {
    setSelectedPositionTemplates([]);
    if (currentCorporationId) {
      loadPositionTemplates(currentCorporationId);
    } else {
      setAvailablePositionTemplates([]);
    }
  }, [currentCorporationId, loadPositionTemplates]);

  // Track corporation changes from form values using useRef to prevent infinite re-renders
  const previousCorporationIdRef = useRef(null);

  const handleFormValuesChange = useCallback((values) => {
    const newCorporationId = values.corporation?._id;
    if (newCorporationId !== previousCorporationIdRef.current) {
      previousCorporationIdRef.current = newCorporationId;
      setCurrentCorporationId(newCorporationId);
    }
  }, []);

  return (
    <Box sx={{ p: 3, maxWidth: '800px', margin: '0 auto' }}>
      <Typography sx={{ mb: 2, fontSize: '24px', fontFamily: PRIMARY_FONT[700] }}>
        {LanguageConverterV2('contract.create.title')}
      </Typography>

      <Form
        onSubmit={handleSubmit}
        render={({ handleSubmit: formSubmit, values }) => {
          // Track corporation changes
          handleFormValuesChange(values);

          return (
            <form onSubmit={formSubmit}>
              {/* Corporation Information Section */}
              <CorporationAgencySection />

              {/* Date Section */}
              <DateSection />

              {/* Position Templates Section */}
              <PositionTemplatesSection
                availablePositionTemplates={availablePositionTemplates}
                selectedPositionTemplates={selectedPositionTemplates}
                onPositionTemplateToggle={handlePositionTemplateToggle}
                onSelectAll={handleSelectAll}
                onClear={handleClear}
                corporationSelected={values.corporation}
              />

              <Divider sx={{ my: 3 }} />

              {/* Rate Overrides Section - Only show for RATE_OVERRIDE contract type */}
              {contractType === CONTRACT_TYPES.RATE_OVERRIDE && <RateOverridesSection />}

              {/* Margin Override Section - Only show for MARGIN_OVERRIDE contract type */}
              {contractType === CONTRACT_TYPES.MARGIN_OVERRIDE && <MarginOverrideSection />}

              <Divider sx={{ my: 3 }} />

              {/* Stipend Section */}
              <StipendSection hasStipend={hasStipend} setHasStipend={setHasStipend} />

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  sx={{ minWidth: '120px' }}
                  disabled={isSubmitting}
                >
                  {LanguageConverterV2('generic.goBack')}
                </Button>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={
                    isSubmitting || selectedPositionTemplates.length === 0 || !values.agency?._id
                  }
                  sx={{
                    minWidth: '120px',
                    backgroundColor: PRIMARY_COLOR[70],
                    '&:hover': {
                      backgroundColor: PRIMARY_COLOR[80],
                    },
                  }}
                >
                  {isSubmitting ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} color="inherit" />
                      {LanguageConverterV2('contract.create.creating')}
                    </Box>
                  ) : (
                    LanguageConverterV2('contract.create.addContract')
                  )}
                </Button>
              </Box>
            </form>
          );
        }}
      />
    </Box>
  );
};

ContractCreate.propTypes = {
  // This component doesn't receive props directly, but uses URL params
  // PropTypes validation is handled by useParams hook
};

export default ContractCreate;
